import { useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { Ref } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import type { StripeCardNumberElementOptions } from '@stripe/stripe-js';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import type { CheckoutErrorDescriptor } from './errorPresentation';
import {
  presentLocalizedError,
  presentProviderError,
  presentSystemError,
} from './errorPresentation';
import { LockIcon } from './icons';
import { stripeLocale, tr, useCurrentLocale } from '../i18n';

export interface StripeCardHandle {
  createPaymentMethod(): Promise<{ token: string } | { error: CheckoutErrorDescriptor }>;
  confirmAction(clientSecret: string): Promise<{ ok: true } | { error: CheckoutErrorDescriptor }>;
}

type StripeCardField = 'cardNumber' | 'expiry' | 'cvc';

interface StripeElementChangeEvent {
  error?: {
    code?: string | undefined;
    message?: string | undefined;
  } | undefined;
}

interface StripeCardFieldsProps {
  ref?: Ref<StripeCardHandle>;
  cardholderName: string;
  onCardholderNameChange: (value: string) => void;
  showSecurity: boolean;
  onActivity?: (() => void) | undefined;
}

function StripeCardFields({
  ref,
  cardholderName,
  onCardholderNameChange,
  showSecurity,
  onActivity,
}: StripeCardFieldsProps) {
  const stripe = useStripe();
  const elements = useElements();
  const focusedField = useRef<StripeCardField | null>(null);
  const [invalidFields, setInvalidFields] = useState<Set<StripeCardField>>(new Set());
  const elementOptions = useMemo<StripeCardNumberElementOptions>(() => ({
    style: {
      base: {
        fontSize: '16px',
        color: '#1a2230',
        fontFamily: 'inherit',
        '::placeholder': { color: '#8a93a3' },
      },
      invalid: { color: '#FF2C2C' },
    },
  }), []);

  function updateFieldInvalid(field: StripeCardField, invalid: boolean) {
    setInvalidFields((current) => {
      const next = new Set(current);
      if (invalid) {
        next.add(field);
      } else {
        next.delete(field);
      }
      return next;
    });
  }

  function handleElementChange(field: StripeCardField, event: StripeElementChangeEvent) {
    if (focusedField.current === field) onActivity?.();
    if (event.error === undefined) {
      updateFieldInvalid(field, false);
      return;
    }
    updateFieldInvalid(field, true);
  }

  useImperativeHandle(
    ref,
    () => ({
      async createPaymentMethod() {
        setInvalidFields(new Set());
        if (!stripe || !elements) {
          return { error: presentSystemError(undefined, 'retry') };
        }
        const cardNumber = elements.getElement(CardNumberElement);
        if (!cardNumber) {
          return { error: presentLocalizedError({ key: 'error.cardDetails', kind: 'system' }) };
        }
        const result = await stripe.createPaymentMethod({
          type: 'card',
          card: cardNumber,
          ...(cardholderName ? { billing_details: { name: cardholderName } } : {}),
        });
        if (result.error) {
          const error = presentProviderError({
            ...(result.error.code !== undefined ? { code: result.error.code } : {}),
            ...(result.error.decline_code !== undefined ? { declineCode: result.error.decline_code } : {}),
            ...(result.error.message !== undefined ? { message: result.error.message } : {}),
            fallbackKey: 'error.cardDetails',
          });
          updateFieldInvalid('cardNumber', true);
          return { error };
        }
        setInvalidFields(new Set());
        return { token: result.paymentMethod.id };
      },
      async confirmAction(clientSecret: string) {
        if (!stripe) {
          return { error: presentSystemError(undefined, 'retry') };
        }
        const result = await stripe.handleNextAction({ clientSecret });
        if (result.error) {
          const error = presentProviderError({
            ...(result.error.code !== undefined ? { code: result.error.code } : {}),
            ...(result.error.decline_code !== undefined ? { declineCode: result.error.decline_code } : {}),
            ...(result.error.message !== undefined ? { message: result.error.message } : {}),
            fallbackKey: 'error.authentication',
          });
          return { error };
        }
        setInvalidFields(new Set());
        return { ok: true as const };
      },
    }),
    [stripe, elements, cardholderName],
  );

  return (
    <div className="vp-panel vp-card-fields">
      {showSecurity && (
        <div className="vp-secure-inline">
          <LockIcon width={14} height={14} /> {tr('payment.secureConnection')}
        </div>
      )}
      <label className={`vp-field ${invalidFields.has('cardNumber') ? 'vp-field--invalid' : ''}`}>
        <span>{tr('payment.cardNumber')}</span>
        <CardNumberElement
          className={`vp-stripe-input ${invalidFields.has('cardNumber') ? 'vp-stripe-input--invalid' : ''}`}
          options={{ ...elementOptions, showIcon: true, disableLink: true }}
          onChange={(event) => handleElementChange('cardNumber', event)}
          onFocus={() => { focusedField.current = 'cardNumber'; }}
          onBlur={() => { focusedField.current = null; }}
        />
      </label>
      <label className="vp-field">
        <span>{tr('payment.cardholderName')}</span>
        <input
          placeholder={tr('payment.cardholderPlaceholder')}
          autoComplete="cc-name"
          value={cardholderName}
          onChange={(event) => onCardholderNameChange(event.target.value)}
        />
      </label>
      <div className="vp-field-row">
        <label className={`vp-field ${invalidFields.has('expiry') ? 'vp-field--invalid' : ''}`}>
          <span>{tr('payment.expiry')}</span>
          <CardExpiryElement
            className={`vp-stripe-input ${invalidFields.has('expiry') ? 'vp-stripe-input--invalid' : ''}`}
            options={elementOptions}
            onChange={(event) => handleElementChange('expiry', event)}
            onFocus={() => { focusedField.current = 'expiry'; }}
            onBlur={() => { focusedField.current = null; }}
          />
        </label>
        <label className={`vp-field ${invalidFields.has('cvc') ? 'vp-field--invalid' : ''}`}>
          <span>{tr('payment.cvv')}</span>
          <CardCvcElement
            className={`vp-stripe-input ${invalidFields.has('cvc') ? 'vp-stripe-input--invalid' : ''}`}
            options={elementOptions}
            onChange={(event) => handleElementChange('cvc', event)}
            onFocus={() => { focusedField.current = 'cvc'; }}
            onBlur={() => { focusedField.current = null; }}
          />
        </label>
      </div>
    </div>
  );
}

interface StripeCardPanelProps extends StripeCardFieldsProps {
  publishableKey: string;
}

export function StripeCardPanel({ publishableKey, ...fields }: StripeCardPanelProps) {
  const locale = useCurrentLocale();
  const stripePromise = useMemo(() => loadStripe(publishableKey), [publishableKey]);

  return (
    <Elements stripe={stripePromise} options={{ locale: stripeLocale() }} key={`${publishableKey}:${locale}`}>
      <StripeCardFields {...fields} />
    </Elements>
  );
}
